import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setError('E-mail ou senha inválidos. Verifique suas credenciais.');
      setLoading(false);
      return;
    }

    // Get user profile to check role
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, is_active')
      .eq('id', data.user.id)
      .maybeSingle();

    if (!profile?.is_active) {
      setError('Sua conta está inativa. Entre em contato com o administrador.');
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Redirect based on role
    if (profile?.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/app');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });

    if (error) {
      setError('Erro ao enviar e-mail de recuperação. Tente novamente.');
    } else {
      setResetSent(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30 flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/8 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-300/6 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div style={{ borderRadius: '12px', overflow: 'hidden', width: 44, height: 44, flexShrink: 0 }}>
              <img
                src="https://storage.readdy-site.link/project_files/4a2f3690-6afc-4752-8f11-296a8cd10aa8/9df4885e-fe7a-4292-927f-532659452029_Gemini_Generated_Image_e0eqt8e0eqt8e0eq.png?v=c10a7e5f8467af600c66ebccd92d7add"
                alt="Granato Tecnologia"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="flex flex-col" style={{ lineHeight: 1.1 }}>
              <span className="text-slate-800 font-bold text-xl tracking-tight whitespace-nowrap">Granato</span>
              <span className="text-cyan-500 font-bold text-xl tracking-tight whitespace-nowrap">Tecnologia</span>
            </div>
          </Link>
          <h1 className="text-slate-800 text-2xl font-bold mb-1">{resetMode ? 'Recuperar Senha' : 'Acesso ao Painel'}</h1>
          <p className="text-slate-500 text-sm">
            {resetMode ? 'Digite seu e-mail para receber o link de recuperação' : 'Entre com suas credenciais para continuar'}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          {error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-200 mb-6">
              <i className="ri-error-warning-line text-red-500 text-lg shrink-0"></i>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {resetSent && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200 mb-6">
              <i className="ri-mail-check-line text-emerald-500 text-lg shrink-0"></i>
              <p className="text-emerald-600 text-sm">E-mail de recuperação enviado! Verifique sua caixa de entrada.</p>
            </div>
          )}

          <form onSubmit={resetMode ? handleResetPassword : handleLogin} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wide">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="seu@email.com"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:bg-white transition-colors"
              />
            </div>

            {!resetMode && (
              <div className="flex flex-col gap-1.5">
                <label className="text-slate-500 text-xs font-medium uppercase tracking-wide">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:bg-white transition-colors"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed mt-1"
              style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin"></i> {resetMode ? 'Enviando...' : 'Entrando...'}</>
              ) : (
                <><i className={resetMode ? 'ri-mail-send-line' : 'ri-login-box-line'}></i> {resetMode ? 'Enviar Link' : 'Entrar no Painel'}</>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <button
              onClick={() => { setResetMode(!resetMode); setError(''); setResetSent(false); }}
              className="text-cyan-500 hover:text-cyan-600 transition-colors font-medium cursor-pointer text-sm"
            >
              {resetMode ? 'Voltar ao login' : 'Esqueceu a senha?'}
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-slate-400 text-sm hover:text-slate-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>Voltar ao site
          </Link>
        </div>
      </div>
    </div>
  );
}
